/**
 * @fileoverview Local-first create-tenant wizard for system-web department onboarding.
 * @module system-web/pages/tenants/wizard/CreateTenantWizard
 *
 * Responsibilities:
 * - Compose the multi-step department onboarding wizard.
 * - Persist onboarding drafts and tenant records through Dexie-first services.
 * - Call the provisioning endpoint only for inherently-online tenant database creation.
 *
 * Dependencies:
 * - onboardingDraftService for local-first draft persistence.
 * - tenantService for local-first tenant writes and identity validation.
 * - apiClient for the provisioning exception route.
 *
 * Architecture:
 * - Local-first React page composition; UI writes admin-authored data to Dexie and reads server provisioning fields only after sync.
 *
 * Notes:
 * - Provisioning-owned fields such as dataLocation are not authored by this component.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import DefensibleMiniLoader from '../../../components/DefensibleMiniLoader';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { WizardState, WizardMode, STEP_LABELS, emptyWizardState } from './types';
import { validateWizardStep, StepError } from './validateStep';
import WizardTabs from './WizardTabs';
import WizardNavigation from './WizardNavigation';
import DepartmentInfoStep from './steps/DepartmentInfoStep';
import OrganizationTierStep from './steps/OrganizationTierStep';
import BillingStep from './steps/BillingStep';
import InspectionPlanStep from './steps/InspectionPlanStep';
import ContactsStep from './steps/ContactsStep';
import ModulesFeaturesStep from './steps/ModulesFeaturesStep';
import RegulatoryStep from './steps/RegulatoryStep';
import AdminAuthStep from './steps/AdminAuthStep';
import InitialDataSetupStep from './steps/InitialDataSetupStep';
import ReviewCreateStep from './steps/ReviewCreateStep';
import {
  createOnboardingDraft,
  updateOnboardingDraft,
  getOnboardingDraft,
  deleteOnboardingDraft,
} from '../../../services/onboardingDraftService';
import { assertTenantIdentityAvailableLocal, deleteTenantLocalOnly, getTenantByOnboardingDraftId, upsertTenant, provisionTenant } from '../../../services/tenantService';
import { triggerDownload } from '../../../services/codeSetVendorService';
import { generateSlug } from '../../../utils/slugify';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbContext';

const TOTAL_STEPS = STEP_LABELS.length;
const AUTOSAVE_DELAY_MS = 1500;

/**
 * @process CreateLocalTenantObjectId
 *
 * Steps:
 * 1. Build a Mongo-compatible timestamp prefix from the browser clock.
 * 2. Append random hexadecimal entropy for a local-first tenant identifier.
 *
 * Inputs:
 * - None.
 *
 * Outputs:
 * - A 24-character hexadecimal tenant identifier suitable for Dexie and MongoDB sync.
 *
 * Failure Handling:
 * - No recoverable runtime failures expected.
 *
 * Idempotency:
 * - NOT REQUIRED.
 */
function createLocalObjectId(): string {
  const timestamp = Math.floor(Date.now() / 1000).toString(16).padStart(8, '0');
  const random = Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${timestamp}${random}`.slice(0, 24);
}

/**
 * @process ResolveWizardTenantIdentity
 *
 * Steps:
 * 1. Prefer NERIS ID as the canonical slug source when present.
 * 2. Fall back to the department name slug for non-NERIS departments.
 * 3. Normalize NERIS ID casing for local and server identity checks.
 *
 * Inputs:
 * - ws: Current local-first onboarding wizard state.
 *
 * Outputs:
 * - slug, nerisId, and internal-department flag used by Dexie and provisioning.
 *
 * Failure Handling:
 * - Missing names fall back to the generic department slug used by existing wizard behavior.
 *
 * Idempotency:
 * - REQUIRED.
 */
function resolveWizardTenantIdentity(ws: WizardState): { slug: string; nerisId: string | null; isInternalDepartment: boolean } {
  const nerisId = ws.departmentInfo?.nerisId?.trim().toUpperCase() || null;
  const slug = nerisId
    ? nerisId.toLowerCase()
    : generateSlug(ws.departmentInfo?.name || 'department');
  const isInternalDepartment = nerisId?.startsWith('SL') ?? false;
  return { slug, nerisId, isInternalDepartment };
}

interface CreateTenantWizardProps {
  mode?: WizardMode;
}

export default function CreateTenantWizard({ mode = 'admin' }: CreateTenantWizardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { draftId: routeDraftId } = useParams<{ draftId?: string }>();
  const { setBreadcrumbLabel } = useBreadcrumbs();

  const [state, setState] = useState<WizardState>({ ...emptyWizardState(), wizardMode: mode });
  const [currentDraftId, setCurrentDraftId] = useState<string | undefined>(routeDraftId);
  const currentDraftIdRef = useRef<string | undefined>(routeDraftId);
  const [loading, setLoading] = useState(!!routeDraftId);
  const [saving, setSaving] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<StepError[]>([]);
  const [errorSteps, setErrorSteps] = useState<number[]>([]);

  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Serialized draft-save queue: updateOnboardingDraft replaces the entire
  // wizardState, so two overlapping saves can finish out of order and let an
  // older write clobber newer choices. Chaining every save (debounced,
  // immediate, manual, pre-provision) guarantees the last write to land is
  // the newest state.
  const saveChainRef = useRef<Promise<unknown>>(Promise.resolve());

  // Update breadcrumb label when department name changes
  useEffect(() => {
    const deptName = state.departmentInfo?.name;
    if (deptName && deptName.trim()) {
      setBreadcrumbLabel(location.pathname, deptName);
    } else {
      setBreadcrumbLabel(location.pathname, 'New Department');
    }
  }, [state.departmentInfo?.name, location.pathname, setBreadcrumbLabel]);

  // Load existing draft from Dexie
  useEffect(() => {
    if (routeDraftId) {
      setLoading(true);
      getOnboardingDraft(routeDraftId)
        .then((draft) => {
          if (draft) {
            setState(draft.wizardState);
            currentDraftIdRef.current = draft._id;
            setCurrentDraftId(draft._id);
            if (draft._id !== routeDraftId) {
              navigate(`/tenants/new/${draft._id}`, { replace: true });
            }
          } else {
            setError('Draft not found');
          }
        })
        .catch((err: any) => setError(err.message || 'Failed to load draft'))
        .finally(() => setLoading(false));
    }
  }, [navigate, routeDraftId]);

  const updateState = useCallback((patch: Partial<WizardState>) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      // Deep-merge departmentInfo to prevent the NERIS polygon fetch callback
      // (which uses a stale snapshot of info captured at call time) from
      // overwriting concurrent jurisdiction/county selections made while the
      // async polygon fetch was in flight. Without this, selecting a county
      // during the fetch window gets clobbered when the callback fires.
      if (patch.departmentInfo && prev.departmentInfo) {
        next.departmentInfo = { ...prev.departmentInfo, ...patch.departmentInfo };
      }
      // Discrete setup choices (radio/checkbox paths under initialDataSetup)
      // flush immediately — the typing debounce only applies to free-text input.
      if (patch.initialDataSetup) {
        if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
        void saveDraftImmediate(next);
        return next;
      }
      // Schedule autosave after user stops typing
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
      autosaveTimer.current = setTimeout(() => {
        saveDraftImmediate(next);
      }, AUTOSAVE_DELAY_MS);
      return next;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Enqueue a draft write behind any in-flight save so writes land in call
  // order. The draft id is read when the task runs, not when it is queued, so
  // a save queued before the first create finishes updates the new draft.
  const enqueueDraftSave = useCallback((ws: WizardState): Promise<void> => {
    const task = async () => {
      if (currentDraftIdRef.current) {
        await updateOnboardingDraft(currentDraftIdRef.current, { wizardState: ws, lastStep: ws.currentStep });
      } else {
        const draft = await createOnboardingDraft(ws);
        currentDraftIdRef.current = draft._id;
        setCurrentDraftId(draft._id);
        navigate(`/tenants/new/${draft._id}`, { replace: true });
      }
    };
    const next = saveChainRef.current.then(task, task);
    saveChainRef.current = next;
    return next;
  }, [navigate]);

  // Immediate save to Dexie (no setSaving spinner — background autosave)
  const saveDraftImmediate = useCallback(async (ws: WizardState) => {
    try {
      await enqueueDraftSave(ws);
    } catch (err) {
      console.error('[wizard] autosave error:', err);
    }
  }, [enqueueDraftSave]);

  // Manual save with spinner (Save Draft button)
  const saveDraft = useCallback(async (ws?: WizardState) => {
    const stateToSave = ws ?? state;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    setSaving(true);
    try {
      await enqueueDraftSave(stateToSave);
    } catch (err: any) {
      console.error('[wizard] save draft error:', err);
    } finally {
      setSaving(false);
    }
  }, [state, enqueueDraftSave]);

  const goToStep = useCallback((step: number) => {
    setState((prev) => ({ ...prev, currentStep: step }));
    setValidationErrors([]);
  }, []);

  const handleNext = useCallback(async () => {
    const errors = validateWizardStep(state.currentStep, state);
    if (state.currentStep === 1) {
      try {
        const identity = resolveWizardTenantIdentity(state);
        await assertTenantIdentityAvailableLocal({
          ...identity,
          excludeDraftId: currentDraftIdRef.current,
        });
      } catch (err: any) {
        errors.push({
          field: 'departmentInfo.name',
          message: err.message || 'Department slug or NERIS ID is already in use',
        });
      }
    }
    setValidationErrors(errors);
    const hardErrors = errors.filter((e) => !e.warning);
    if (hardErrors.length > 0) {
      setErrorSteps((prev) => prev.includes(state.currentStep) ? prev : [...prev, state.currentStep]);
      return;
    }

    // Clear error marker for this step on success
    setErrorSteps((prev) => prev.filter((s) => s !== state.currentStep));

    const nextStep = Math.min(state.currentStep + 1, TOTAL_STEPS);
    const newState: WizardState = {
      ...state,
      currentStep: nextStep,
      completedSteps: state.completedSteps.includes(state.currentStep)
        ? state.completedSteps
        : [...state.completedSteps, state.currentStep],
    };
    setState(newState);
    await saveDraft(newState);
  }, [state, saveDraft]);

  const handleBack = useCallback(() => {
    if (state.currentStep > 1) {
      setState((prev) => ({ ...prev, currentStep: prev.currentStep - 1 }));
      setValidationErrors([]);
    }
  }, [state.currentStep]);

  const handleProvision = useCallback(async () => {
    const finalState: WizardState = {
      ...state,
      completedSteps: state.completedSteps.includes(TOTAL_STEPS)
        ? state.completedSteps
        : [...state.completedSteps, TOTAL_STEPS],
    };
    setState(finalState);

    // Ensure draft exists in Dexie before calling provision API
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    setSaving(true);
    try {
      await enqueueDraftSave(finalState);
    } catch (err: any) {
      setError('Failed to save draft before provisioning: ' + (err.message || ''));
      setSaving(false);
      return;
    }
    setSaving(false);

    const draftIdToUse = currentDraftIdRef.current;
    if (!draftIdToUse) {
      setError('Failed to create draft. Please try again.');
      return;
    }

    setProvisioning(true);
    setError(null);
    try {
      const now = new Date().toISOString();
      const isTrial = finalState.billing?.subscriptionType === 'trial';
      const existingDraftTenant = draftIdToUse
        ? await getTenantByOnboardingDraftId(draftIdToUse)
        : null;
      const tenantId = existingDraftTenant?._id ?? createLocalObjectId();
      
      const { slug, nerisId, isInternalDepartment } = resolveWizardTenantIdentity(finalState);
      const selectedAuthMethod = finalState.authenticationSettings?.authMethod ?? 'local';
      const tenantAuthSettings: WizardState['authenticationSettings'] = selectedAuthMethod !== 'local'
        ? {
            ...finalState.authenticationSettings,
            authMethod: 'local',
            stagedAuthMethod: selectedAuthMethod,
            setupStatus: 'draft',
          }
        : {
            ...(finalState.authenticationSettings ?? {}),
            authMethod: 'local',
            setupStatus: 'draft',
          };
      const provisionState: WizardState = {
        ...finalState,
        authenticationSettings: tenantAuthSettings,
      };
      await assertTenantIdentityAvailableLocal({
        slug,
        nerisId,
        excludeTenantId: tenantId,
        excludeDraftId: draftIdToUse,
      });

      // Construct billing and trial data to match backend structure
      const subscriptionStatus = isTrial ? 'trial' : 'active';
      const trialData = isTrial ? {
        departmentContact: {
          name: finalState.trial?.departmentContact?.name || '',
          email: finalState.trial?.departmentContact?.email || '',
          phone: finalState.trial?.departmentContact?.phone || undefined,
        },
        startDate: now,
        endDate: new Date(new Date(now).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days default
        durationDays: 30,
        supportLevel: finalState.trial?.supportLevel || 'basic',
        salesNotes: finalState.trial?.salesNotes || '',
        extensions: [],
      } : undefined;

      // Parcel import + update token entitlement — mirror the server-side
      // provisioning logic (onboardingRoutes.ts lines 772-860) so the local
      // Dexie tenant record immediately has these fields. Without this, the
      // DepartmentParcelDataTab shows "No configuration" until a sync pull
      // reconciles from the server (which may also be missing them if the
      // Docker container is running older code).
      const parcelImport = finalState.initialDataSetup?.parcelImport;
      const isRegridPath = parcelImport?.path === 'regrid';
      const tokensPerYear = typeof parcelImport?.tokensPerYear === 'number'
        ? Math.max(1, Math.min(12, parcelImport.tokensPerYear))
        : 1;
      const updateTokens = isRegridPath
        ? {
            tokensPerYear,
            renewalStart: now,
            renewalEnd: undefined as unknown as string, // Set at first renewal cycle
            tokensUsed: 0,
            carriedOverBalance: 0,
            currentVersion: null,
            lastUpdateDate: null,
          }
        : undefined;

      const localTenant = {
        id: tenantId,
        _id: tenantId,
        name: finalState.departmentInfo?.name,
        slug,
        status: 'provisioning',
        isInternalDepartment,
        nerisId,
        // dataLocation intentionally excluded - backend has complete version
        organizationId: finalState.organizationId ?? null,
        tier: finalState.tier ?? 'professional',
        address: finalState.departmentInfo?.address,
        timezone: finalState.departmentInfo?.timezone ?? 'America/New_York',
        departmentType: finalState.departmentInfo?.departmentType ?? 'career',
        departmentClassification: finalState.departmentInfo?.departmentClassification ?? 'municipal',
        nerisVerification: finalState.departmentInfo?.nerisVerification ?? null,
        // Persist the legacy Streamline link in the CANONICAL tenant shape
        // (isLinked / databaseServer) so isTenantEligibleForMigration can resolve
        // legacyDb + legacyDeptId. The wizard state uses the draft shape
        // (isExistingClient / database), so map it here. Previously omitted entirely,
        // which hid onboarded departments from the Legacy Migration picker.
        legacySystem: finalState.legacySystem?.isExistingClient
          ? {
              isLinked: true,
              databaseServer: finalState.legacySystem.database,
              departmentId: finalState.legacySystem.departmentId,
              departmentData: finalState.legacySystem.departmentData ?? {},
              linkedAt: now,
            }
          : { isLinked: false },
        boundary: finalState.departmentInfo?.selectedBoundary?.geometry ?? null,
        boundaryCentroid: finalState.departmentInfo?.selectedBoundary?.centroid ?? null,
        boundaryBbox: finalState.departmentInfo?.selectedBoundary?.bbox ?? null,
        boundaryArea: finalState.departmentInfo?.selectedBoundary?.area ?? null,
        branding: finalState.branding ?? {},
        billing: {
          billingEntity: finalState.billing?.billingEntity ?? 'department',
          subscriptionType: isTrial ? 'trial' : 'active',
          billingCycle: finalState.billing?.billingCycle ?? 'annual',
          billingContact: finalState.billing?.billingContact ?? { name: '', email: '', phone: '' },
          fiscalYear: finalState.billing?.fiscalYear ?? { startMonth: 1, startDay: 1 },
          startDate: finalState.billing?.startDate ?? now.split('T')[0],
          subscription: {
            status: subscriptionStatus,
            startDate: now,
            currentPeriodStart: now,
            currentPeriodEnd: isTrial
              ? trialData?.endDate
              : new Date(new Date(now).getTime() + 365 * 24 * 60 * 60 * 1000).toISOString(),
            autoRenew: !isTrial,
          },
        },
        trial: trialData,
        inspectionPlan: finalState.inspectionPlan ?? {},
        contacts: finalState.contacts ?? [],
        modules: finalState.modules ?? [],
        jurisdiction: finalState.departmentInfo?.jurisdiction ?? {},
        regulatoryFramework: {
          ...(finalState.regulatory ?? {}),
          codeSetVendors: (finalState.regulatory?.vendors ?? []).map(v => ({
            vendorId: v.vendorId,
            vendorName: v.vendorId === 'icc' ? 'ICC Digital Codes' : v.vendorId === 'nfpa' ? 'NFPA LiNK' : 'Custom Vendor',
            isEnabled: true,
            credentials: {
              clientId: v.clientId,
              clientSecret: v.clientSecret,
              apiKey: v.apiKey,
              subscriptionId: v.subscriptionId,
            },
            // Seed selectedBooks with the wizard selection so the Code Sets
            // tab immediately shows them as `pending`. The post-provisioning
            // enqueue below will turn each one into a BackgroundJob; the
            // worker upserts the same selectedBooks entries with progress.
            selectedBooks: (v.selectedBooks ?? []).map(b => ({
              bookId: b.bookId,
              title: b.title,
              edition: b.edition,
              downloadStatus: 'pending',
              downloadPhase: 'queued',
              progressPercent: 0,
              processedRecords: 0,
              violationCount: 0,
              requestedAt: now,
            })),
            lastValidationStatus: 'untested',
            addedAt: new Date().toISOString(),
            addedBy: 'onboarding-wizard',
          })),
        },
        dataRetentionPolicy: finalState.dataRetentionPolicy ?? {},
        notificationPreferences: finalState.notificationPreferences ?? {},
        mapDefaults: finalState.mapDefaults ?? {},
        authenticationSettings: tenantAuthSettings,
        onboarding: {
          status: 'in_progress',
          startedAt: now,
          lastStep: 10,
          draftId: draftIdToUse,
          initialDataSetup: finalState.initialDataSetup,
          initialAdminName: [finalState.initialAdmin?.firstName, finalState.initialAdmin?.lastName].filter(Boolean).join(' ') || undefined,
          initialAdminEmail: finalState.initialAdmin?.email || undefined,
          initialAdminInviteDeferred: finalState.initialAdmin?.sendInviteImmediately === false,
        },
        ...(updateTokens ? { updateTokens } : {}),
        ...(parcelImport ? { parcelImport } : {}),
        createdAt: now,
        updatedAt: now,
      };

      await upsertTenant(localTenant);

      const result = await provisionTenant(draftIdToUse, provisionState, isTrial, tenantId);
      const provisionedTenantId = result._id ?? tenantId;

      if (provisionedTenantId !== tenantId) {
        await deleteTenantLocalOnly(tenantId);
      }

      await upsertTenant({
        ...localTenant,
        _id: provisionedTenantId,
        id: provisionedTenantId,
        slug: result.slug ?? localTenant.slug,
        name: result.name ?? localTenant.name,
        status: result.status ?? localTenant.status,
        updatedAt: new Date().toISOString(),
      });

      // Remove the draft from local Dexie — it is now a live tenant
      await deleteOnboardingDraft(draftIdToUse);

      // Fire-and-forget: enqueue one code-set download per book selected in
      // the Regulatory step. Each enqueue creates a `backgroundJobs` row and
      // the user immediately sees progress in the sidebar tether indicator.
      // Failures here are non-fatal — the user can re-trigger from the
      // department's Code Sets tab.
      const vendorsForDownload = (finalState.regulatory?.vendors ?? []).filter(
        (v) => v.vendorId === 'icc' && (v.selectedBooks?.length ?? 0) > 0,
      );
      for (const vendor of vendorsForDownload) {
        for (const book of vendor.selectedBooks ?? []) {
          try {
            await triggerDownload(provisionedTenantId, {
              vendorId: vendor.vendorId,
              bookId: book.bookId,
              edition: book.edition,
              title: book.title,
            });
          } catch (enqErr) {
            console.warn(
              `[CreateTenantWizard] code-set-download enqueue failed for ${vendor.vendorId}/${book.bookId}:`,
              enqErr,
            );
          }
        }
      }

      navigate('/tenants', { state: { created: finalState.departmentInfo?.name } });
    } catch (err: any) {
      if (err.validationErrors?.length) {
        const lines = err.validationErrors.flatMap((ve: any) =>
          (ve.errors ?? []).map((e: any) => {
            const path = Array.isArray(e.path) && e.path.length > 0 ? ` (${e.path.join('.')})` : '';
            return `Step ${ve.step}${path}: ${e.message ?? JSON.stringify(e)}`;
          })
        );
        setError(`Draft has validation errors:\n${lines.join('\n')}`);
      } else {
        setError(err.message || 'Failed to provision department');
      }
    } finally {
      setProvisioning(false);
    }
  }, [currentDraftId, state, navigate, enqueueDraftSave]);

  const renderStep = () => {
    const props = { state, onChange: updateState, errors: validationErrors };
    switch (state.currentStep) {
      case 1: return <DepartmentInfoStep {...props} />;
      case 2: return <OrganizationTierStep {...props} />;
      case 3: return <ContactsStep {...props} />;
      case 4: return <BillingStep {...props} />;
      case 5: return <InspectionPlanStep {...props} />;
      case 6: return <RegulatoryStep {...props} />;
      case 7: return <ModulesFeaturesStep {...props} />;
      case 8: return <AdminAuthStep {...props} />;
      case 9: return <InitialDataSetupStep {...props} />;
      case 10: return <ReviewCreateStep state={state} onEditStep={goToStep} />;
      default: return null;
    }
  };

  if (loading) {
    return <DefensibleMiniLoader />;
  }

  const isLastStep = state.currentStep === TOTAL_STEPS;

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center gap-4 px-6 py-4 border-b border-gray-200 bg-white shrink-0">
        <button
          onClick={() => navigate('/tenants')}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Back to tenants"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-gray-900">
            {routeDraftId ? 'Resume Onboarding' : 'New Department'}
          </h1>
          <p className="text-xs text-gray-500 truncate">
            {state.departmentInfo?.name
              ? `Setting up ${state.departmentInfo.name}`
              : 'Complete all steps to provision the department'}
            {currentDraftId && (
              <span className="ml-2 inline-flex items-center gap-1 text-gray-400">
                <Save className="h-3 w-3" />
                Draft saved
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Horizontal tab bar */}
      <WizardTabs
        currentStep={state.currentStep}
        completedSteps={state.completedSteps}
        errorSteps={errorSteps}
        onStepClick={goToStep}
      />

      {/* Error banner */}
      {error && (() => {
        const lines = error.split('\n');
        return (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              {lines.length > 1 ? (
                <>
                  <p className="font-medium mb-1">{lines[0]}</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    {lines.slice(1).map((l, i) => <li key={i}>{l}</li>)}
                  </ul>
                </>
              ) : (
                <span>{error}</span>
              )}
            </div>
            <button
              onClick={() => setError(null)}
              className="shrink-0 text-red-500 hover:text-red-700 font-medium"
            >
              Dismiss
            </button>
          </div>
        );
      })()}

      {/* Validation errors / warnings */}
      {validationErrors.length > 0 && (() => {
        const hardErrors = validationErrors.filter((e) => !e.warning);
        const warnings = validationErrors.filter((e) => e.warning);
        return (
          <>
            {hardErrors.length > 0 && (
              <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                <p className="text-sm font-medium text-red-800 mb-1">
                  Please fix {hardErrors.length} error{hardErrors.length > 1 ? 's' : ''} before continuing:
                </p>
                <ul className="list-disc list-inside text-sm text-red-700 space-y-0.5">
                  {hardErrors.map((e, i) => <li key={i}>{e.message}</li>)}
                </ul>
              </div>
            )}
            {warnings.length > 0 && (
              <div className="mx-6 mt-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                <p className="text-sm font-medium text-amber-800 mb-1">
                  {warnings.length} incomplete field{warnings.length > 1 ? 's' : ''} — you can continue, but must complete before provisioning:
                </p>
                <ul className="list-disc list-inside text-sm text-amber-700 space-y-0.5">
                  {warnings.map((e, i) => <li key={i}>{e.message}</li>)}
                </ul>
              </div>
            )}
          </>
        );
      })()}

      {/* Step content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-6">
          {renderStep()}
        </div>
      </div>

      {/* Footer navigation */}
      <WizardNavigation
        currentStep={state.currentStep}
        totalSteps={TOTAL_STEPS}
        onBack={handleBack}
        onNext={handleNext}
        onSaveDraft={() => saveDraft()}
        onProvision={handleProvision}
        saving={saving}
        provisioning={provisioning}
      />
    </div>
  );
}
